import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { formatCurrency } from "@/utils/analyticsUtils";

export const fetchServices = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return [];
    }

    const { data: servicesData, error: servicesError } = await supabase
      .from('services')
      .select(`
        *,
        service_categories (
          id,
          name
        )
      `)
      .eq('user_id', user.id)  // Filter by the current user's ID
      .order('created_at', { ascending: false });

    if (servicesError) {
      console.error("Services fetch error:", servicesError);
      throw new Error("Failed to fetch services");
    }

    return servicesData || [];
  } catch (error) {
    console.error('Error fetching services:', error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "Failed to fetch services. Please try again.",
    });
    return [];
  }
};

export const deleteService = async (id: string): Promise<boolean> => {
  try {
    console.log("Attempting to delete service with ID:", id);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("No user found for service deletion");
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to delete services.",
      });
      return false;
    }

    // Check if there are any service requests for this service
    const { data: serviceRequests, error: requestsError } = await supabase
      .from('service_requests')
      .select('id')
      .eq('service_id', id)
      .limit(1);
      
    if (requestsError) {
      console.error("Error checking for service requests:", requestsError);
    } else if (serviceRequests && serviceRequests.length > 0) {
      // Service has existing requests, suggest hiding instead
      toast({
        variant: "destructive",
        title: "Cannot Delete Service",
        description: "This service has existing requests and cannot be deleted. You can hide it instead.",
      });
      return false;
    }

    // First, delete any associated service images
    const { error: imagesError } = await supabase
      .from('service_images')
      .delete()
      .eq('service_id', id);
      
    if (imagesError) {
      console.error("Error deleting service images:", imagesError);
      // Continue with service deletion even if image deletion fails
    }

    // Then delete the service itself
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);  // Ensure users can only delete their own services

    if (error) {
      console.error("Error deleting service:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete service. Please try again.",
      });
      return false;
    }

    console.log("Service deleted successfully");
    toast({
      title: "Success",
      description: "Service deleted successfully.",
    });
    return true;
  } catch (error) {
    console.error("Exception in deleteService:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "An unexpected error occurred during deletion.",
    });
    return false;
  }
};

export const updateServiceCategory = async (serviceId: string, categoryId: string): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    toast({
      variant: "destructive",
      title: "Error",
      description: "You must be logged in to update services.",
    });
    return false;
  }

  const { error } = await supabase
    .from('services')
    .update({ category_id: categoryId })
    .eq('id', serviceId)
    .eq('user_id', user.id);  // Ensure users can only update their own services

  if (error) {
    toast({
      variant: "destructive",
      title: "Error",
      description: "Failed to update service category. Please try again.",
    });
    return false;
  }

  toast({
    title: "Success",
    description: "Service category updated successfully.",
  });
  return true;
};

// Format the price based on currency
export const formatPrice = (price: number, locale?: string | null, currency?: string | null): string => {
  // Default to KES if no currency is provided
  const currencyCode = currency || "KES";
  const fallbackLocale = 'en-US';
  
  try {
    // Check if Intl is available
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      return new Intl.NumberFormat(locale || fallbackLocale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(price);
    }
  } catch (e) {
    console.error('Error formatting price with Intl:', e);
  }
  
  // Fallback formatting if Intl is not available or fails
  return `${currencyCode} ${price.toFixed(2)}`;
};

// Calculate total price including travel fee if applicable
export const calculateTotalPrice = (
  basePrice: number, 
  discountedPrice: number | null | undefined, 
  travelFee: number | null | undefined, 
  serviceMode: string | undefined
): number => {
  // Determine the actual service price (with discount if available)
  const servicePrice = (discountedPrice !== undefined && discountedPrice !== null && discountedPrice > 0) 
    ? discountedPrice 
    : basePrice;
  
  // Add travel fee if service mode is in-person and a fee is set
  const fee = (serviceMode === 'in_person' && travelFee && travelFee > 0) ? travelFee : 0;
  
  return servicePrice + fee;
};

export const updateProfileSettings = async (
  updates: { 
    currency?: string; 
    preferred_messaging?: string;
    company_name?: string;
    whatsapp_number?: string;
  }
): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to update profile settings.",
      });
      return false;
    }

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) {
      console.error("Error updating profile settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update profile settings. Please try again.",
      });
      return false;
    }

    toast({
      title: "Success",
      description: "Profile settings updated successfully.",
    });
    return true;
  } catch (error) {
    console.error("Exception in updateProfileSettings:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "An unexpected error occurred during update.",
    });
    return false;
  }
};

// Toggle service visibility (is_active field)
export const updateServiceVisibility = async (serviceId: string, isActive: boolean): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to update services.",
      });
      return false;
    }

    const { error } = await supabase
      .from('services')
      .update({ is_active: isActive })
      .eq('id', serviceId)
      .eq('user_id', user.id);  // Ensure users can only update their own services

    if (error) {
      console.error("Error updating service visibility:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update service visibility. Please try again.",
      });
      return false;
    }

    toast({
      title: "Success",
      description: `Service ${isActive ? 'visible' : 'hidden'} successfully.`,
    });
    return true;
  } catch (error) {
    console.error("Exception in updateServiceVisibility:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "An unexpected error occurred during update.",
    });
    return false;
  }
};

// Toggle service auto-approval setting
export const updateServiceAutoApproval = async (serviceId: string, autoApprove: boolean): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to update services.",
      });
      return false;
    }

    const { error } = await supabase
      .from('services')
      .update({ instant_booking: autoApprove })
      .eq('id', serviceId)
      .eq('user_id', user.id);  // Ensure users can only update their own services

    if (error) {
      console.error("Error updating service auto-approval setting:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update service settings. Please try again.",
      });
      return false;
    }

    toast({
      title: "Success",
      description: `Service ${autoApprove ? 'set to auto-approve' : 'will require manual approval'}.`,
    });
    return true;
  } catch (error) {
    console.error("Exception in updateServiceAutoApproval:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "An unexpected error occurred during update.",
    });
    return false;
  }
};

/**
 * Fetches the top N most requested services for the current user
 * @param limit Number of top services to fetch (default 3)
 * @returns Array of services with request counts
 */
export const fetchTopRequestedServices = async (limit: number = 3) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // First, get all service requests for the user
    const { data: serviceRequests, error: requestError } = await supabase
      .from('service_requests')
      .select(`
        service_id,
        services (
          id,
          name,
          price
        )
      `)
      .eq('user_id', user.id);

    if (requestError) {
      console.error('Error fetching service requests:', requestError);
      return [];
    }

    // Count and group requests by service_id manually
    const serviceCounts = new Map();
    const serviceData = new Map();
    
    // Process service requests to count and collect service data
    serviceRequests.forEach(request => {
      if (!request.service_id || !request.services) return;
      
      // Count occurrences
      const currentCount = serviceCounts.get(request.service_id) || 0;
      serviceCounts.set(request.service_id, currentCount + 1);
      
      // Store service data (only needs to be done once per service)
      if (!serviceData.has(request.service_id)) {
        serviceData.set(request.service_id, {
          id: request.services.id,
          name: request.services.name,
          price: request.services.price
        });
      }
    });
    
    // Convert to array, sort, and limit
    const topServices = Array.from(serviceCounts.entries())
      .map(([serviceId, count]) => ({
        id: serviceId,
        name: serviceData.get(serviceId)?.name,
        price: serviceData.get(serviceId)?.price,
        requestCount: count
      }))
      .sort((a, b) => b.requestCount - a.requestCount)
      .slice(0, limit);
    
    return topServices;
  } catch (error) {
    console.error('Exception fetching top requested services:', error);
    return [];
  }
};
